import React, { useState, useEffect, useCallback } from "react";
import {
   DatePicker, TimePicker, Button, Badge, Tag,
  Typography, Divider, Avatar
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { showLoading, hideLoading } from "../redux/alertsSlice";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import moment from "moment";
import Layout from "../components/layout";
import {
   EnvironmentOutlined, MedicineBoxOutlined,
  CalendarOutlined, ClockCircleOutlined, CheckCircleOutlined,
  ArrowRightOutlined, StarFilled
} from "@ant-design/icons";

const { Title, Text } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD   = "#0d9488";
const TEAL      = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE     = "#0f172a";
const SLATE_MID = "#334155";
const MUTED     = "#94a3b8";
const SURFACE   = "#f8fafc";
const WHITE     = "#ffffff";

/* ─── Style injection ────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("book-appt-styles")) return;
  const el = document.createElement("style");
  el.id = "book-appt-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .ba-root { font-family: 'DM Sans', sans-serif; background: ${SURFACE}; min-height: 100vh; }

    /* Left panel */
    .ba-left-panel {
      background: linear-gradient(160deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      border-radius: 24px 0 0 24px;
      padding: 48px 40px;
      position: relative; overflow: hidden;
      min-height: 600px;
      display: flex; flex-direction: column; justify-content: space-between;
    }
    .ba-left-panel::before {
      content: '';
      position: absolute; top: -80px; right: -80px;
      width: 320px; height: 320px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}44 0%, transparent 70%);
      pointer-events: none; filter: blur(32px);
    }
    .ba-left-panel::after {
      content: '';
      position: absolute; bottom: -60px; left: -60px;
      width: 240px; height: 240px; border-radius: 50%;
      background: radial-gradient(circle, ${EMERALD}33 0%, transparent 70%);
      pointer-events: none; filter: blur(28px);
    }

    /* Right panel */
    .ba-right-panel {
      background: ${WHITE};
      border-radius: 0 24px 24px 0;
      padding: 48px 40px;
      display: flex; flex-direction: column; justify-content: center;
    }

    /* Info row */
    .ba-info-row {
      display: flex; align-items: flex-start; gap: 12px;
      padding: 12px 0;
      border-bottom: 1px solid rgba(255,255,255,.08);
    }
    .ba-info-row:last-child { border-bottom: none; }

    .ba-icon-wrap {
      width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
      background: rgba(20,184,166,.15);
      display: flex; align-items: center; justify-content: center;
      color: ${TEAL}; font-size: 16px;
    }

    /* Day badge */
    .ba-day-badge {
      display: inline-block;
      padding: 4px 12px; border-radius: 20px;
      background: rgba(20,184,166,.15);
      color: #5eead4;
      font-size: 12px; font-weight: 600;
      border: 1px solid rgba(20,184,166,.3);
      transition: background .2s;
    }
    .ba-day-badge:hover { background: rgba(20,184,166,.28); }

    /* Picker wrapper */
    .ba-picker-wrap .ant-picker {
      width: 100%; border-radius: 12px; height: 48px;
      border: 1.5px solid #e2e8f0;
      font-family: 'DM Sans', sans-serif; font-size: 14px;
      transition: border-color .2s, box-shadow .2s;
    }
    .ba-picker-wrap .ant-picker:hover,
    .ba-picker-wrap .ant-picker-focused {
      border-color: ${TEAL} !important;
      box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    /* CTA button */
    .ba-btn {
      height: 52px !important; width: 100%;
      border-radius: 14px !important;
      font-size: 15px !important; font-weight: 700 !important;
      font-family: 'Sora', sans-serif !important;
      letter-spacing: .3px;
      border: none !important;
      transition: transform .2s, box-shadow .2s !important;
    }
    .ba-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px ${TEAL}55 !important; }
    .ba-btn-check {
      background: linear-gradient(135deg, ${EMERALD}, ${TEAL}) !important;
      color: ${WHITE} !important;
    }
    .ba-btn-book {
      background: linear-gradient(135deg, #16a34a, #22c55e) !important;
      color: ${WHITE} !important;
    }

    /* Availability badge */
    .ba-available-pill {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 6px 14px; border-radius: 20px;
      background: #dcfce7; color: #16a34a;
      font-size: 13px; font-weight: 600;
      margin-bottom: 16px;
    }

    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .ba-fade { animation: fadeUp .5s ease both; }
    .ba-fade-1 { animation-delay: .05s; }
    .ba-fade-2 { animation-delay: .12s; }
    .ba-fade-3 { animation-delay: .2s; }

    @media (max-width: 768px) {
      .ba-left-panel { border-radius: 24px 24px 0 0; padding: 36px 24px; min-height: unset; }
      .ba-right-panel { border-radius: 0 0 24px 24px; padding: 36px 24px; }
    }
  `;
  document.head.appendChild(el);
};

/* ─── Component ──────────────────────────────────────────────── */
function BookAppointment() {
  const [isAvailable, setIsAvailable] = useState(false);
  const navigate = useNavigate();
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(moment().format("HH:mm"));
  const { user } = useSelector((state) => state.user);
  const params = useParams();
  const [doctor, setDoctor] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => { injectStyles(); }, []);

  const getDoctorData = useCallback(async () => {
    try {
      dispatch(showLoading());
      const res = await axios.post(
        "/api/doctor/get-doctor-info-by-id",
        { doctorId: params.doctorId },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (res.data.success) setDoctor(res.data.data);
    } catch { dispatch(hideLoading()); }
  }, [dispatch, params.doctorId]);

  useEffect(() => { getDoctorData(); }, [getDoctorData]);

  const checkAvailability = async () => {
    try {
      dispatch(showLoading());
      const res = await axios.post(
        "/api/bookAppointment/check-availability",
        { doctorId: params.doctorId, date, time },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (res.data.success) {
        toast.success(res.data.message);
        setIsAvailable(true);
      } else {
        toast.error(res.data.message);
      }
    } catch {
      toast.error("Error checking availability!");
      dispatch(hideLoading());
    }
  };

  const bookNow = async () => {
    if (!date || !time || time === "00:00") {
      toast.error("Please select a valid time.");
      return;
    }
    const formattedTime = moment(time, "HH:mm").format("HH:mm");
    setIsAvailable(false);
    try {
      dispatch(showLoading());
      const res = await axios.post(
        "/api/bookAppointment/book-appointment",
        { doctorId: params.doctorId, userId: user._id, doctorInfo: doctor, userInfo: user, date, time: formattedTime },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (res.data.success) {
        toast.success(res.data.message);
        setTimeout(() => navigate("/appointments"), 1500);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      dispatch(hideLoading());
      toast.error(error.response?.data?.message || "Error booking appointment!");
    }
  };

  const handleDateChange = (value) => {
    setDate(moment(value).format("DD-MM-YYYY"));
    setIsAvailable(false);
  };

  const handleTimeChange = (value) => {
    setTime(value ? moment(value, "HH:mm").format("HH:mm") : null);
  };

  return (
    <Layout>
      <div className="ba-root">
        <div style={{ maxWidth: 940, margin: "40px auto", padding: "0 20px 60px" }}>

          {/* Page header */}
          <div className="ba-fade" style={{ marginBottom: 32, textAlign: "center" }}>
            <div style={{
              display: "inline-block", padding: "4px 14px", borderRadius: 20,
              background: TEAL_LIGHT, color: EMERALD,
              fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase",
              marginBottom: 12, fontFamily: "'Sora', sans-serif"
            }}>
              <CalendarOutlined style={{ marginRight: 5 }} />Book an Appointment
            </div>
            <Title level={2} style={{ margin: 0, fontFamily: "'Sora', sans-serif", color: SLATE, fontWeight: 800 }}>
              Schedule Your Visit
            </Title>
            <Text style={{ color: MUTED, fontSize: 14 }}>
              Review the doctor's details and pick your preferred date below
            </Text>
          </div>

          {doctor && (
            <div
              className="ba-fade ba-fade-1"
              style={{
                borderRadius: 24, overflow: "hidden",
                boxShadow: "0 24px 64px rgba(0,0,0,.1)",
                display: "flex", flexWrap: "wrap",
              }}
            >
              {/* ── Left: Doctor info ── */}
              <div className="ba-left-panel" style={{ flex: "1 1 340px" }}>
                <div style={{ position: "relative", zIndex: 1 }}>
                  {/* Avatar + name */}
                  <div style={{ marginBottom: 32 }}>
                    <Avatar
                      size={72}
                      style={{
                        background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`,
                        fontSize: 50, fontWeight: 700,
                        boxShadow: `0 8px 24px ${TEAL}55`,
                        marginBottom: 10, display: "flex"
                      }}
                    >
                      {doctor.firstName?.[0]}
                    </Avatar>
                    <Title level={3} style={{ margin: 0, color: WHITE, fontFamily: "'Sora', sans-serif", fontWeight: 700 }}>
                      Dr. {doctor.firstName} {doctor.lastName}
                    </Title>
                    <div style={{ marginTop: 6 }}>
                      <Tag
                        style={{
                          background: `${TEAL}33`, color: "#5eead4",
                          border: `1px solid ${TEAL}44`, borderRadius: 20,
                          fontSize: 12, fontWeight: 600
                        }}
                      >
                        {doctor.specialization || doctor.service || "Specialist"}
                      </Tag>
                    </div>
                  </div>

                  {/* Info rows */}
                  <div>
                    <div className="ba-info-row">
                      <div className="ba-icon-wrap"><MedicineBoxOutlined /></div>
                      <div>
                        <Text style={{ color: MUTED, fontSize: 11, display: "block", fontWeight: 500 }}>SERVICE</Text>
                        <Text style={{ color: WHITE, fontSize: 14 }}>{doctor.service}</Text>
                      </div>
                    </div>

                    <div className="ba-info-row">
                      <div className="ba-icon-wrap"><EnvironmentOutlined /></div>
                      <div>
                        <Text style={{ color: MUTED, fontSize: 11, display: "block", fontWeight: 500 }}>LOCATION</Text>
                        <Text style={{ color: WHITE, fontSize: 14 }}>{doctor.location}</Text>
                      </div>
                    </div>

                    <div className="ba-info-row">
                      <div className="ba-icon-wrap"><ClockCircleOutlined /></div>
                      <div>
                        <Text style={{ color: MUTED, fontSize: 11, display: "block", fontWeight: 500 }}>CONSULTATION HOURS</Text>
                        <Text style={{ color: WHITE, fontSize: 14 }}>
                          {doctor.appointmentTime?.[0]} – {doctor.appointmentTime?.[1]}
                        </Text>
                      </div>
                    </div>

                    <div className="ba-info-row" style={{ flexDirection: "column" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                        <div className="ba-icon-wrap"><CalendarOutlined /></div>
                        <div>
                          <Text style={{ color: MUTED, fontSize: 11, display: "block", fontWeight: 500 }}>AVAILABLE DAYS</Text>
                          <Badge
                            count={doctor.clinicDay?.length}
                            style={{ background: EMERALD, fontSize: 10 }}
                          >
                            <Text style={{ color: WHITE, fontSize: 13, paddingRight: 10 }}>
                              {doctor.clinicDay?.length} day{doctor.clinicDay?.length !== 1 ? "s" : ""}
                            </Text>
                          </Badge>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingLeft: 46 }}>
                        {doctor.clinicDay?.map((day, i) => (
                          <span key={i} className="ba-day-badge">{day}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom decoration */}
                <div style={{ position: "relative", zIndex: 1, marginTop: 24 }}>
                  <Divider style={{ borderColor: "rgba(255,255,255,.1)", margin: "0 0 16px" }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <StarFilled style={{ color: "#fbbf24", fontSize: 14 }} />
                    <StarFilled style={{ color: "#fbbf24", fontSize: 14 }} />
                    <StarFilled style={{ color: "#fbbf24", fontSize: 14 }} />
                    <StarFilled style={{ color: "#fbbf24", fontSize: 14 }} />
                    <StarFilled style={{ color: "#fbbf24", fontSize: 14 }} />
                    <Text style={{ color: MUTED, fontSize: 12, marginLeft: 4 }}>Verified Doctor</Text>
                  </div>
                </div>
              </div>

              {/* ── Right: Booking form ── */}
              <div className="ba-right-panel" style={{ flex: "1 1 340px" }}>
                <div className="ba-fade ba-fade-2">
                  <Title level={4} style={{ margin: "0 0 4px", fontFamily: "'Sora', sans-serif", color: SLATE, fontWeight: 700 }}>
                    Pick Your Date
                  </Title>
                  <Text style={{ color: MUTED, fontSize: 13 }}>
                    Choose an available date within the doctor's schedule
                  </Text>

                  <Divider style={{ margin: "20px 0" }} />

                  {/* Date picker */}
                  <div style={{ marginBottom: 20 }}>
                    <Text strong style={{ color: SLATE_MID, fontSize: 13, display: "block", marginBottom: 8 }}>
                      <CalendarOutlined style={{ marginRight: 6, color: EMERALD }} />
                      Appointment Date
                    </Text>
                    <div className="ba-picker-wrap">
                      <DatePicker
                        format="DD-MM-YYYY"
                        onChange={handleDateChange}
                        placeholder="Select a date"
                        style={{ width: "100%" }}
                        disabledDate={current => current && current < moment().startOf("day")}
                      />
                    </div>
                  </div>

                  {/* Hidden time picker — kept for API compat */}
                  <TimePicker
                    format="HH:mm"
                    onChange={handleTimeChange}
                    value={time ? moment(time, "HH:mm") : null}
                    style={{ display: "none" }}
                  />

                  {/* Availability status */}
                  {isAvailable && (
                    <div className="ba-available-pill ba-fade">
                      <CheckCircleOutlined />
                      Slot available — ready to book!
                    </div>
                  )}

                  {/* CTA */}
                  <div className="ba-fade ba-fade-3" style={{ marginTop: 8 }}>
                    {!isAvailable ? (
                      <Button
                        className="ba-btn ba-btn-check"
                        onClick={checkAvailability}
                        disabled={!date}
                        icon={<ClockCircleOutlined />}
                      >
                        Check Availability
                      </Button>
                    ) : (
                      <Button
                        className="ba-btn ba-btn-book"
                        onClick={bookNow}
                        icon={<ArrowRightOutlined />}
                      >
                        Confirm Booking
                      </Button>
                    )}
                  </div>

                  {/* Helper text */}
                  <Text style={{ color: MUTED, fontSize: 12, marginTop: 14, display: "block", textAlign: "center" }}>
                    You'll receive a confirmation once the doctor approves your appointment
                  </Text>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default BookAppointment;